import type { FaqItem } from '../components/faq-accordion';
import { INTENT_HREF, salesLink } from '../components/cta';
import { icon } from '../components/icons';
import { routeHref } from '../lib/links';

/** Enterprise FAQ (sections 31–35). Answers are trusted, in-repo HTML. */
export const FAQ = {
  office: [
    'Can ReTax360 be configured for each office?',
    '<p>Yes. ReTax360 workflows can be configured around different offices, markets, roles, stages, approval requirements and business rules while maintaining a shared enterprise foundation. Organizations can preserve local operating differences while maintaining centralized visibility and governance.</p>',
  ],
  juris: [
    'Which jurisdictions do you support?',
    `<p>ReTax360 supports property tax operations across North America, including the United States and Canada. The platform is designed so workflows and operating requirements can be configured around different jurisdictions rather than requiring every market to operate identically. Organizations operating outside North America can <a href="${INTENT_HREF['enterprise-contact']}" data-cta="contact_enterprise_team">contact our enterprise team</a> to discuss their requirements.</p>`,
  ],
  approvals: [
    'How do human approvals work?',
    '<p>AI can extract, classify, draft, recommend and prepare work. Business rules and orchestration determine where that work should move next. At defined approval points, designated professionals can review, approve, modify or reject AI-assisted outputs before they become part of the official workflow or trigger critical actions. AI prepares. Rules govern. Experts decide.</p>',
  ],
  data: [
    'Who owns our data?',
    `<p>You do. Customer operational data remains the customer's property. ReTax360 provides the platform, orchestration infrastructure, automation framework and underlying technology used to process and manage that information. Customer operational data—including case records, settlement information, reporting, financial records and organization-specific configurations—remains owned by the customer according to the applicable enterprise agreement.</p><p><a class="tlink" href="${routeHref('/security#data-protection')}">Learn about data protection ${icon('arrow')}</a></p>`,
  ],
  training: [
    'Is client data used to train AI models?',
    '<p>Identifiable customer data is not used as general-purpose training data. Where permitted under the applicable enterprise agreement, anonymized, aggregated and de-identified information may be used to improve platform intelligence and model performance, provided that the information cannot be attributed to the customer, its clients, a specific property or a specific transaction. Organizations with specific AI-data requirements can address those requirements during the enterprise agreement and security review.</p>',
  ],
  sso: [
    'Do you support Single Sign-On?',
    '<p>Yes. ReTax360 supports enterprise Single Sign-On requirements. Identity-provider configuration and implementation requirements are reviewed during enterprise onboarding.</p>',
  ],
  pilot: [
    'What does a pilot look like?',
    '<p>A ReTax360 pilot begins with a defined workflow, office, market or operational use case. Together, we establish:</p><ul class="bullets"><li>workflow scope</li><li>participating users</li><li>approval requirements</li><li>operational objectives</li><li>success criteria</li></ul><p>The pilot is then deployed with real workflows and human reviewers in the loop. Results are evaluated against the agreed criteria before moving into a phased production rollout.</p><p class="mono small">Discover → Configure → Pilot → Measure → Roll out → Expand</p>',
  ],
  pricing: [
    'How is pricing structured?',
    `<p>ReTax360 is an enterprise platform. Pricing is based on deployment scope, workflows, organizational requirements, integrations and implementation needs.</p><p>${salesLink()}</p>`,
  ],
} as const satisfies Record<string, FaqItem>;

export type FaqKey = keyof typeof FAQ;

export const FAQ_ALL: readonly FaqKey[] = [
  'office',
  'juris',
  'approvals',
  'data',
  'training',
  'sso',
  'pilot',
  'pricing',
];

export const faqItems = (keys: readonly FaqKey[]): FaqItem[] => keys.map((key) => FAQ[key]);
